import React from "react";
import {useNavigate} from "react-router-dom";
import styles from "../../styles/dashboard.module.css";

export default function BobWidget({name, data, bobMetrics, currentStatus, unacknowledgedAlarms, isDashboardItem, isDashboardEditMode}) {
    const navigate = useNavigate();

    const goto = () => {
        if (isDashboardItem && isDashboardEditMode) return;
        localStorage.setItem('device', JSON.stringify(data));
        navigate('/variables');
    };

    const getAnomalyColor = (level) => {
        if (level == null) return '#9E9E9E';
        if (level >= 50) return '#F43F5E';
        if (level >= 25) return '#F59E0B';
        return '#22C55E';
    };

    const getSemaphoreStatus = () => {
        if (!bobMetrics || bobMetrics.anomaly_level == null) return 'none';
        if (bobMetrics.anomaly_level >= 50) return 'high';
        if (bobMetrics.anomaly_level >= 25) return 'medium';
        return 'low';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'start': return '#4CAF50';
            case 'stop': return '#F44336';
            default: return '#9E9E9E';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'start': return 'Operating';
            case 'stop': return 'Stopped';
            default: return 'Unknown';
        }
    };

    const semaphore = getSemaphoreStatus();
    const anomalyLevel = bobMetrics?.anomaly_level;

    return (
        <div className={styles.variable_box} style={{
            padding: '12px 16px',
            cursor: isDashboardEditMode ? 'move' : 'pointer',
            height: '100%',
            maxHeight: 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
        }} onClick={goto}>
            {/* Header */}
            <div className={'d-flex justify-content-between align-items-center mb-2'}>
                <div className={'d-flex align-items-center'}>
                    <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        backgroundColor: getStatusColor(currentStatus),
                        marginRight: 8,
                    }}/>
                    <div className={styles.variable_name} style={{fontSize: '16px'}}>
                        {name || data?.name || 'BOB ASSISTANT'}
                    </div>
                </div>
                {unacknowledgedAlarms > 0 && (
                    <div style={{
                        backgroundColor: '#FEF2F2',
                        color: '#EF4444',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                    }}>
                        {unacknowledgedAlarms} alarms
                    </div>
                )}
            </div>

            {/* Status text */}
            <div style={{color: '#64748B', fontSize: '12px', marginBottom: '8px'}}>
                {getStatusText(currentStatus)}
            </div>

            {/* Metrics Grid */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px'}}>
                <div style={{
                    backgroundColor: anomalyLevel != null ? `${getAnomalyColor(anomalyLevel)}15` : '#f8fafc',
                    padding: '6px 8px',
                    borderRadius: '6px',
                }}>
                    <div style={{fontSize: '10px', color: '#64748B'}}>Anomaly</div>
                    <div style={{fontSize: '16px', fontWeight: 600, color: getAnomalyColor(anomalyLevel)}}>
                        {anomalyLevel != null ? `${anomalyLevel.toFixed(1)}%` : '--'}
                    </div>
                </div>
                <div style={{backgroundColor: '#f8fafc', padding: '6px 8px', borderRadius: '6px'}}>
                    <div style={{fontSize: '10px', color: '#64748B'}}>Temperature</div>
                    <div style={{fontSize: '16px', fontWeight: 600, color: '#334155'}}>
                        {bobMetrics?.temperature != null ? `${bobMetrics.temperature.toFixed(1)}°C` : '--'}
                    </div>
                </div>
                <div style={{backgroundColor: '#f8fafc', padding: '6px 8px', borderRadius: '6px'}}>
                    <div style={{fontSize: '10px', color: '#64748B'}}>Vibration</div>
                    <div style={{fontSize: '16px', fontWeight: 600, color: '#334155'}}>
                        {bobMetrics?.vibration_level != null ? bobMetrics.vibration_level.toFixed(2) : '--'}
                    </div>
                </div>
                <div style={{backgroundColor: '#f8fafc', padding: '6px 8px', borderRadius: '6px'}}>
                    <div style={{fontSize: '10px', color: '#64748B'}}>Battery</div>
                    <div style={{fontSize: '16px', fontWeight: 600, color: '#334155'}}>
                        {bobMetrics?.battery_percentage != null ? `${bobMetrics.battery_percentage}%` : '--'}
                    </div>
                </div>
            </div>

            {/* Semaphore */}
            <div className={`d-flex flex-row justify-content-between w-100 ${styles.severityRow}`}>
                <div className={styles.variable_severity_container} style={{
                    backgroundColor: semaphore === 'high' ? '#FFE2E5' : '#f8fafc',
                    height: '40px',
                }}>
                    <div className="d-flex align-items-center" style={{gap: '4px'}}>
                        <img src={semaphore === 'high' ? '/alerts/high.png' : '/alerts/high_disabled.png'} alt={'high'} className={styles.alertIcon} style={{height: '20px'}}/>
                        <div className={styles.variable_severity_text}>Danger</div>
                    </div>
                </div>
                <div className={styles.variable_severity_container} style={{
                    backgroundColor: semaphore === 'medium' ? '#FFF4DE' : '#f8fafc',
                    height: '40px',
                }}>
                    <div className="d-flex align-items-center" style={{gap: '4px'}}>
                        <img src={semaphore === 'medium' ? '/alerts/medium.png' : '/alerts/medium_disabled.png'} alt={'medium'} className={styles.alertIcon} style={{height: '20px'}}/>
                        <div className={styles.variable_severity_text}>Warning</div>
                    </div>
                </div>
                <div className={styles.variable_severity_container} style={{
                    backgroundColor: semaphore === 'low' ? '#DCFCE7' : '#f8fafc',
                    height: '40px',
                }}>
                    <div className="d-flex align-items-center" style={{gap: '4px'}}>
                        <img src={semaphore === 'low' ? '/alerts/low.png' : '/alerts/low_disabled.png'} alt={'low'} className={styles.alertIcon} style={{height: '20px'}}/>
                        <div className={styles.variable_severity_text}>OK</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
